import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO, differenceInDays } from 'date-fns';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import LeaveRequestModal from './LeaveRequestModal';
import LeaveCycleModal from './LeaveCycleModal';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiCheck, FiX, FiCalendar, FiFilter, FiBriefcase, FiEdit2, FiTrash2, FiRefreshCw, FiSave, FiAlertTriangle, FiClock } = FiIcons;

// Helper function for status colors
const getStatusColor = (status) => {
  switch (status) {
    case 'approved': return 'bg-green-100 text-green-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    default: return 'bg-yellow-100 text-yellow-800';
  }
};

// Extracted LeaveCard component
const LeaveCard = ({ leave, isManagerView, teamMembers, onUpdateStatus, onEdit, onDelete }) => {
  const requester = teamMembers.find(m => m.id === leave.userId);
  
  // Calculate duration display
  let durationDisplay = '';
  if (leave.isHalfDay) {
    durationDisplay = '0.5 Days (Half Day)';
  } else {
    const start = new Date(leave.startDate);
    const end = new Date(leave.endDate);
    const days = differenceInDays(end, start) + 1;
    durationDisplay = `${days} Day${days > 1 ? 's' : ''}`;
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-all duration-200"
    >
      <div className="flex justify-between items-start">
        <div className="space-y-2 flex-1">
          {isManagerView && (
            <div className="flex items-center space-x-2 mb-2">
              <img src={requester?.avatar || 'https://via.placeholder.com/32'} alt={requester?.name} className="w-6 h-6 rounded-full" />
              <span className="font-medium text-gray-900">{requester?.name || 'Unknown User'}</span>
            </div>
          )}
          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
            <span className="font-semibold text-gray-800">{leave.type}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(leave.status)}`}>
              {leave.status}
            </span>
            {leave.isHalfDay && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex items-center">
                <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                Half Day
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <SafeIcon icon={FiCalendar} className="w-4 h-4" />
              <span>
                {format(new Date(leave.startDate), 'MMM dd')}
                {!leave.isHalfDay && leave.startDate !== leave.endDate && ` - ${format(new Date(leave.endDate), 'MMM dd, yyyy')}`}
                {leave.isHalfDay && `, ${new Date(leave.startDate).getFullYear()}`}
              </span>
            </div>
            <span className="text-gray-400">•</span>
            <span className="font-medium text-gray-700">{durationDisplay}</span>
          </div>
          <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded italic border border-gray-100">
            "{leave.reason}"
          </p>
        </div>
        <div className="flex flex-col space-y-2 ml-4">
          {isManagerView && leave.status === 'pending' && (
            <div className="flex space-x-2">
              <button
                onClick={() => onUpdateStatus(leave.id, 'approved')}
                className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                title="Approve"
              >
                <SafeIcon icon={FiCheck} className="w-5 h-5" />
              </button>
              <button
                onClick={() => onUpdateStatus(leave.id, 'rejected')}
                className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                title="Reject"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
          )}
          {!isManagerView && leave.status === 'pending' && (
            <div className="flex space-x-2">
              <button
                onClick={() => onEdit(leave)}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit Request"
              >
                <SafeIcon icon={FiEdit2} className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(leave);
                }}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Cancel Request"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const LeaveManagement = () => {
  const { leaves, teamMembers, requestLeave, updateLeaveStatus, deleteLeave, updateLeave, bulkUpdateLeaveBalances, bulkExpireLeaves, updateTeamMember } = useData();
  const { user, hasPermission } = useAuth();
  
  const [showModal, setShowModal] = useState(false);
  const [showCycleModal, setShowCycleModal] = useState(false);
  const [leaveToEdit, setLeaveToEdit] = useState(null);
  const [leaveToDelete, setLeaveToDelete] = useState(null); // New state for delete modal
  const [activeTab, setActiveTab] = useState('my-leaves');
  const [filterMonth, setFilterMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [filterType, setFilterType] = useState('all');

  // Admin specific states for Cycle Management
  const [expirySettings, setExpirySettings] = useState({ type: 'casual', limit: 0 });
  const [editBalance, setEditBalance] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Get current user's leave balance
  const currentUserMember = teamMembers.find(m => m.id === user.id);
  const leaveBalance = currentUserMember?.leaveBalance || { casual: 0, paid: 0 };

  const filterLeaves = (leaveList) => {
    return leaveList.filter(l => {
      let matchesMonth = true;
      if (filterMonth) {
        const leaveDate = parseISO(l.startDate);
        matchesMonth = format(leaveDate, 'yyyy-MM') === filterMonth;
      }
      let matchesType = true;
      if (filterType !== 'all') {
        matchesType = l.type === filterType;
      }
      return matchesMonth && matchesType;
    });
  };

  const myLeaves = filterLeaves(leaves.filter(l => l.userId === user.id));
  const teamLeaves = filterLeaves(leaves.filter(l => l.userId !== user.id));

  const handleRequestLeave = (data) => {
    requestLeave(data);
    setShowModal(false);
  };

  const handleUpdateLeave = (data) => {
    if (leaveToEdit) {
      updateLeave(leaveToEdit.id, data);
    }
    setLeaveToEdit(null);
    setShowModal(false);
  };

  // Trigger Delete Modal
  const confirmDelete = (leave) => {
    setLeaveToDelete(leave);
  };

  // Execute Delete
  const executeDelete = async () => {
    if (leaveToDelete) {
      try {
        await deleteLeave(leaveToDelete.id);
        setLeaveToDelete(null);
      } catch (error) {
        console.error("Error deleting leave:", error);
        alert("Failed to delete request.");
      }
    }
  };

  const openEditModal = (leave) => {
    setLeaveToEdit(leave);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setLeaveToEdit(null);
    setShowModal(true);
  };

  // Cycle Management
  const handleCycleConfirm = (selectedIds, casualAdd, paidAdd) => {
    bulkUpdateLeaveBalances(selectedIds, casualAdd, paidAdd);
    setSuccessMessage('Leave balances updated successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleExpiry = () => {
    const actionText = expirySettings.limit === 0 ? 'RESET to 0' : `CAP at ${expirySettings.limit}`;
    if (window.confirm(`Are you sure you want to ${actionText} the ${expirySettings.type} leave balance for ALL employees?`)) {
      bulkExpireLeaves(expirySettings.type, Number(expirySettings.limit));
      setSuccessMessage(`${expirySettings.type} leaves processed successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const startEditingBalance = (member) => {
    setEditBalance({
      id: member.id,
      casual: member.leaveBalance?.casual || 0,
      paid: member.leaveBalance?.paid || 0
    });
  };

  const saveBalance = () => {
    if (editBalance) {
      updateTeamMember(editBalance.id, {
        leaveBalance: {
          casual: parseInt(editBalance.casual),
          paid: parseInt(editBalance.paid)
        }
      });
      setEditBalance(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leave Management</h1>
          <p className="text-gray-600 mt-1">Manage time off, cycles, and balances</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          <span>Request Leave</span>
        </button>
      </div>

      {activeTab !== 'cycles' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-2 text-gray-600 mb-2">
              <div className="p-1.5 bg-blue-100 rounded-lg">
                <SafeIcon icon={FiBriefcase} className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium">Casual Leaves Left</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{leaveBalance.casual}</p>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center space-x-2 text-gray-600 mb-2">
              <div className="p-1.5 bg-purple-100 rounded-lg">
                <SafeIcon icon={FiCheck} className="w-4 h-4 text-purple-600" />
              </div>
              <span className="text-sm font-medium">Paid Leaves Left</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{leaveBalance.paid}</p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-2 rounded-xl border border-gray-200 shadow-sm gap-2 sm:gap-0">
        <div className="flex w-full sm:w-auto border-b sm:border-b-0 border-gray-200 mb-2 sm:mb-0">
          <button
            onClick={() => setActiveTab('my-leaves')}
            className={`flex-1 sm:flex-none px-6 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'my-leaves' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            My Leaves
          </button>
          {(hasPermission('view_team') || hasPermission('all')) && (
            <button
              onClick={() => setActiveTab('team-leaves')}
              className={`flex-1 sm:flex-none px-6 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'team-leaves' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Team Requests
              {teamLeaves.filter(l => l.status === 'pending').length > 0 && (
                <span className="ml-2 bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">
                  {teamLeaves.filter(l => l.status === 'pending').length}
                </span>
              )}
            </button>
          )}
          {hasPermission('all') && (
            <button
              onClick={() => setActiveTab('cycles')}
              className={`flex-1 sm:flex-none px-6 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'cycles' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Balances & Cycles
            </button>
          )}
        </div>

        {activeTab !== 'cycles' && (
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full sm:w-auto border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="Sick Leave">Sick Leave</option>
                <option value="Casual Leave">Casual Leave</option>
                <option value="Annual Leave">Annual Leave</option>
                <option value="Unpaid Leave">Unpaid Leave</option>
              </select>
            </div>
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="w-full sm:w-auto border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div>
        {activeTab === 'cycles' ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {successMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 text-green-700 px-4 py-3 rounded-lg flex items-center shadow-sm border border-green-200"
              >
                <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" />
                {successMessage}
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <SafeIcon icon={FiRefreshCw} className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Run Monthly Cycle</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    Credit casual and paid leave days to employees.
                  </p>
                </div>
                <button
                  onClick={() => setShowCycleModal(true)}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                  <span>Open Cycle Wizard</span>
                </button>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Expire / Reset Leaves</h3>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Reset balances or apply carry-forward limits.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={expirySettings.type}
                      onChange={(e) => setExpirySettings({ ...expirySettings, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="casual">Casual</option>
                      <option value="paid">Paid</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Limit</label>
                    <input
                      type="number"
                      min="0"
                      value={expirySettings.limit}
                      onChange={(e) => setExpirySettings({ ...expirySettings, limit: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0 = Reset"
                    />
                  </div>
                </div>
                <button
                  onClick={handleExpiry}
                  className="w-full px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 font-medium transition-colors"
                >
                  Process Expiry
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                <h3 className="font-semibold text-gray-900">Employee Leave Balances</h3>
                <span className="text-xs text-gray-500">{teamMembers.length} employees</span>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Employee</th>
                    <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Casual</th>
                    <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">Paid</th>
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {teamMembers.map(member => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <img src={member.avatar} alt="" className="w-8 h-8 rounded-full" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{member.name}</p>
                            <p className="text-xs text-gray-500">{member.role.replace('_', ' ')}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {editBalance?.id === member.id ? (
                          <input
                            type="number"
                            value={editBalance.casual}
                            onChange={(e) => setEditBalance({ ...editBalance, casual: e.target.value })}
                            className="w-16 text-center border rounded py-1"
                          />
                        ) : (
                          <span className="text-sm font-medium text-gray-900">{member.leaveBalance?.casual || 0}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {editBalance?.id === member.id ? (
                          <input
                            type="number"
                            value={editBalance.paid}
                            onChange={(e) => setEditBalance({ ...editBalance, paid: e.target.value })}
                            className="w-16 text-center border rounded py-1"
                          />
                        ) : (
                          <span className="text-sm font-medium text-gray-900">{member.leaveBalance?.paid || 0}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {editBalance?.id === member.id ? (
                          <div className="flex justify-end space-x-2">
                            <button onClick={saveBalance} className="text-green-600 hover:text-green-700">
                              <SafeIcon icon={FiSave} className="w-5 h-5" />
                            </button>
                            <button onClick={() => setEditBalance(null)} className="text-gray-400 hover:text-gray-600">
                              <SafeIcon icon={FiX} className="w-5 h-5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditingBalance(member)}
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {activeTab === 'my-leaves' ? (
              myLeaves.length > 0 ? (
                myLeaves.map(leave => (
                  <LeaveCard 
                    key={leave.id} 
                    leave={leave} 
                    teamMembers={teamMembers}
                    onUpdateStatus={updateLeaveStatus}
                    onEdit={openEditModal}
                    onDelete={confirmDelete}
                  />
                ))
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                  <SafeIcon icon={FiCalendar} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No leave history found</p>
                </div>
              )
            ) : (
              teamLeaves.length > 0 ? (
                teamLeaves.map(leave => (
                  <LeaveCard 
                    key={leave.id} 
                    leave={leave} 
                    isManagerView={true} 
                    teamMembers={teamMembers}
                    onUpdateStatus={updateLeaveStatus}
                    onEdit={openEditModal}
                    onDelete={confirmDelete}
                  />
                ))
              ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                  <SafeIcon icon={FiCalendar} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No team leave requests found</p>
                </div>
              )
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <LeaveRequestModal
            leaveToEdit={leaveToEdit}
            onClose={() => setShowModal(false)}
            onSubmit={leaveToEdit ? handleUpdateLeave : handleRequestLeave}
          />
        )}
        {showCycleModal && (
          <LeaveCycleModal
            isOpen={showCycleModal}
            onClose={() => setShowCycleModal(false)}
            teamMembers={teamMembers}
            onConfirm={handleCycleConfirm}
          />
        )}
        {/* Delete Confirmation Modal */}
        {leaveToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.target === e.currentTarget && setLeaveToDelete(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden p-6 text-center"
            >
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTrash2} className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Cancel Leave Request?</h3>
              <p className="text-gray-600 mb-6 text-sm">
                Are you sure you want to delete this request for <strong>{leaveToDelete.type}</strong>? This action cannot be undone.
              </p>
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => setLeaveToDelete(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Keep Request
                </button>
                <button
                  onClick={executeDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  Yes, Cancel It
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeaveManagement;